import { Components } from '.generated/templates/Feature.Components.model';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import LinkA11yWrapper from 'components/helpers/LinkA11yWrapper';
import { ImageField, LinkField, useSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { classnames } from 'tailwindcss-classnames';
import { pageorLink } from 'components/authorable/site/Footer/Footer';
import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import ArrowDown from 'components/authorable/site/Header/SvgArrowDown/arrow-down';
import { useScreenBreakpoint } from 'lib/util/useScreenWidth';
import { FieldValue } from 'components/helpers/SitecoreForm/FieldProps';
import { TabNavigation, TabTopContainer } from './NmdpSiteHeaderTablet';
import DesktopTopContainer from './NmdpSiteHeaderDesktop';
import { hasSubmenu } from './NmdpSiteHeaderClasses';
import {
  NavigationLink,
  NavigationItem,
  headerOrLink,
  CollectionStackItem,
} from './NmdpSiteHeaderTypes';
import {
  menuLocation,
  pageNavigationDataLayerPush,
  pageNavigationDataLayerHandler,
  logoNavigationGtmPush,
} from 'lib/util/gtm-datalayer/';
import {
  MobileTopContainer,
  MobileNavigation,
  MobileBottomContainer,
} from './NmdpSiteHeaderMobile';
import getDesktopItems from './NmdpSiteHeaderDesktopNav';
import NmdpSiteHeaderSkeleton from './NmdpSiteHeaderSkeleton';

export type NmdpHeaderProps = {
  fields?: {
    root?: Components.Site.Fields.Header & {
      utilityLinks?: pageorLink[];
      children?: headerOrLink[] & {
        children?: NavigationItem[];
      };
      id: string;
      fields: {
        searchPlaceholderText?: FieldValue;
        giveMoneyButton?: LinkField;
        giveButtonMobileLabel: {
          value?: string;
        };
        joinRegistryButton?: LinkField;
        joinButtonMobileLabel: {
          value?: string;
        };
      };
    };
  };
};

const textWhite = `text-white`;
const svgArrowDown = `svg-arrow-down`;
const floatRight = `float-right`;
const mobileDropMenuItem = classnames('block', 'w-full', 'p-4');

const backBtnClass = classnames(
  'back-btn',
  mobileDropMenuItem,
  'font-medium',
  'pt-2.5',
  'text-blue-navy',
  'text-left',
  'border-l-8',
  'border-green'
);

const mobileBtn = classnames(mobileDropMenuItem, 'font-medium', 'text-left', textWhite);
const buttonArrow = classnames(svgArrowDown, 'inline-block');
const backBtnArrow = classnames(buttonArrow, 'float-left', '-ml-2', 'rotate-90');
const mobileArrow = classnames(buttonArrow, floatRight, '-rotate-90', 'ml-auto');

const getItemDisplayName = (item: NavigationLink): string => {
  const navHeadingValue = item?.fields?.navigationHeading?.value?.trim() ?? '';
  return navHeadingValue.length > 0 ? navHeadingValue : item?.displayName?.trim() ?? '';
};

const NmdpSiteHeader = ({ fields }: NmdpHeaderProps): JSX.Element => {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [searchIsOpen, setSearchIsOpen] = useState(false);
  const [openSubMenuId, setOpenSubMenuId] = useState<string | null>(null);
  const [shouldKeepSubMenuOpen, setShouldKeepSubMenuOpen] = useState(false); // State to track whether submenu should stay open
  const [collectionStack, setCollectionStack] = useState<CollectionStackItem[]>([]);

  // Use the new useScreenBreakpoint hook
  const { isSmallMobileScreen, isMobileScreen, isTabletScreen, isDesktopScreen, isInitializing } =
    useScreenBreakpoint();

  const utilityLinks = fields?.root?.fields?.utilityLinks as pageorLink[];
  const giveMoneyButton = fields?.root?.fields?.giveMoneyButton as LinkField;
  const giveButtonMobileLabel =
    fields?.root?.fields?.giveButtonMobileLabel?.value ?? giveMoneyButton?.value?.text ?? '';
  const joinRegistryButton = fields?.root?.fields?.joinRegistryButton as LinkField;
  const joinButtonMobileLabel =
    fields?.root?.fields?.joinButtonMobileLabel?.value ?? joinRegistryButton?.value?.text ?? '';
  const context = useSitecoreContext();
  const isOnSearchPage = context.sitecoreContext.hasSearch === true;
  const logoText = fields?.root?.fields?.logoText.value || '';

  useEffect(() => {
    setIsOpen(false);
    setOpenSubMenuId(null);
    document.body.className = 'show-body';
  }, [router?.asPath]);
  useEffect(() => {
    setCollectionStack([
      {
        linkItems: fields?.root?.children as NavigationLink[],
        backToDisplayName: '',
      },
    ]);
  }, [fields?.root?.children]);
  useEffect(() => {
    function handleOutsideClick(event: MouseEvent) {
      if (
        event.target instanceof HTMLElement &&
        !(event.target.tagName === 'A' && event.target.classList.contains('submenu-open')) &&
        !event.target.closest('.submenu-open')
      ) {
        if (!shouldKeepSubMenuOpen) {
          setOpenSubMenuId(null);
        }
        return;
      }
      setShouldKeepSubMenuOpen(false);
    }
    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [shouldKeepSubMenuOpen, setOpenSubMenuId]);

  const closeSearch = useCallback(() => {
    if (searchIsOpen) {
      setSearchIsOpen(false);
    }
    logoNavigationGtmPush(menuLocation[0]);
  }, [searchIsOpen]);

  const root = fields?.root;
  const logo = fields?.root?.fields?.logo as ImageField;
  const placeholderText = fields?.root?.fields.searchPlaceholderText?.value as string;
  if (!fields) {
    return <MissingDataSource />;
  }

  // Show loading skeleton while initializing to prevent layout shift
  if (isInitializing) {
    return <NmdpSiteHeaderSkeleton />;
  }
  const toggleMobileMenu = () => {
    setIsOpen((prev) => !prev);
    document.body.className = isOpen ? 'show-body' : 'hide-body';
    setSearchIsOpen(false);
    setCollectionStack([
      {
        linkItems: root?.children as NavigationLink[],
        backToDisplayName: '',
      },
    ]);
    setOpenSubMenuId(null);
    const displayText = isOpen ? 'Close' : 'Menu';
    pageNavigationDataLayerPush(displayText, menuLocation[1]);
  };
  const toggleSearchMobileMenu = () => {
    setSearchIsOpen((prev) => !prev);
    setIsOpen(false);
    document.body.className = 'show-body';
    pageNavigationDataLayerPush('Search', menuLocation[1]);
  };
  const toggleSubMenu = (navId: string | null, displayText: string) => {
    if (openSubMenuId === navId) {
      setOpenSubMenuId(null);
      setShouldKeepSubMenuOpen(false);
    } else {
      setOpenSubMenuId(navId);
      setShouldKeepSubMenuOpen(true);
      pageNavigationDataLayerPush(displayText, menuLocation[1]);
    }
  };
  const getMobileItems = (orgLinkItems: NavigationLink[] = []) => {
    return orgLinkItems?.map((item: NavigationLink) => {
      const onItemClick = (e: React.MouseEvent<HTMLOrSVGElement>) => {
        const displayText = getItemDisplayName(item);
        e.stopPropagation();
        if (item.children) {
          const newItems: CollectionStackItem[] = [
            ...collectionStack,
            {
              linkItems: item.children,
              backToDisplayName: displayText,
              fields: item.fields,
            },
          ];
          setCollectionStack(newItems);
        }
        pageNavigationDataLayerPush(displayText, menuLocation[1]);
      };
      if (item.fields?.navigationItemLink) {
        return (
          <li
            role="menuitem"
            key={item.id}
            className={classnames(
              'xl:block',
              mobileDropMenuItem,
              'border-b',
              'border-gray-whisper',
              'text-blue',
              'font-regular'
            )}
            aria-label="Menu item"
          >
            <LinkA11yWrapper
              key={item.id}
              editable={false}
              field={item.fields.navigationItemLink}
              id={`nav-links--${item.id}`}
              onClick={toggleMobileMenu}
              onGtmPush={(e) => {
                pageNavigationDataLayerHandler(e);
              }}
            />
          </li>
        );
      }
      if (item.fields?.navigationLink && collectionStack.length === 2) {
        return (
          <li
            key={item.id}
            className={`${classnames(
              'xl:border-b',
              mobileDropMenuItem,
              'border-b',
              'border-gray-whisper',
              'text-blue',
              'font-medium',
              'flex',
              'items-center'
            )} menu-item`}
          >
            <LinkA11yWrapper
              className={classnames('hover:border-opacity-0')}
              key={item.id}
              editable={false}
              field={item.fields?.navigationLink}
              id={`nav-links--${item.id}`}
              suppressLinkText
            >
              {getItemDisplayName(item)}
            </LinkA11yWrapper>
            {item?.children?.length === 0 ? (
              ''
            ) : (
              <ArrowDown
                onClick={onItemClick}
                aria-hidden="true"
                className={classnames(mobileArrow)}
                style={{ width: '40px', height: '40px' }}
              />
            )}
          </li>
        );
      }

      return (
        <li role="menuitem" key={item.id} className={hasSubmenu}>
          <button onClick={onItemClick} type="button" className={classnames(mobileBtn)}>
            <div className={classnames('flex', 'items-center')}>
              {getItemDisplayName(item)}
              <ArrowDown
                aria-hidden="true"
                className={classnames(mobileArrow)}
                style={{ width: '40px', height: '40px' }}
              />
            </div>
          </button>
        </li>
      );
    });
  };

  const getBackButton = () => {
    const backHandler = () => {
      const backItems = collectionStack.reduce(
        (acc: CollectionStackItem[], item: CollectionStackItem, inx: number) => {
          if (inx < collectionStack.length - 1) {
            acc.push(item);
          }
          return acc;
        },
        []
      );
      setCollectionStack(backItems);
    };
    if (collectionStack.length > 1) {
      const backButtonText = collectionStack[collectionStack.length - 1].backToDisplayName || '';
      return (
        <button type="button" onClick={backHandler} className={classnames(backBtnClass)}>
          <div className={classnames('flex', 'items-center')}>
            <ArrowDown
              aria-hidden="true"
              className={classnames(backBtnArrow)}
              style={{ width: '40px', height: '40px' }}
            />
            {backButtonText}
          </div>
        </button>
      );
    }
    return <></>;
  };
  const lastItem = collectionStack[collectionStack.length - 1];
  const mobileItems = getMobileItems(lastItem?.linkItems);
  const desktopItems = getDesktopItems(
    collectionStack[0]?.linkItems,
    openSubMenuId,
    toggleSubMenu,
    getItemDisplayName
  );
  return (
    <div className="nmdp-header">
      {isMobileScreen && (
        <MobileTopContainer
          toggleMobileMenu={toggleMobileMenu}
          toggleSearchMobileMenu={toggleSearchMobileMenu}
          closeSearch={closeSearch}
          isOpen={isOpen}
          searchIsOpen={searchIsOpen}
          logo={logo}
          logoText={logoText}
          searchPage={isOnSearchPage}
          giveMoneyButton={giveMoneyButton}
          joinRegistryButton={joinRegistryButton}
          isSmallMobileScreen={isSmallMobileScreen}
        />
      )}
      {isTabletScreen && (
        <TabTopContainer
          placeholderText={placeholderText}
          toggleMobileMenu={toggleMobileMenu}
          isOpen={isOpen}
          logo={logo}
          logoText={logoText}
          giveMoneyButton={giveMoneyButton}
          joinRegistryButton={joinRegistryButton}
        />
      )}
      <div className={classnames('xl:block', 'max-w-screen-2xl', 'mx-auto')}>
        {isMobileScreen && (
          <MobileNavigation
            isOpen={isOpen}
            collectionStack={collectionStack}
            getBackButton={getBackButton}
            mobileItems={mobileItems}
            searchIsOpen={searchIsOpen}
            placeholderText={placeholderText}
            utilityLinks={utilityLinks}
          />
        )}
        {isTabletScreen && (
          <TabNavigation
            isOpen={isOpen}
            collectionStack={collectionStack}
            getBackButton={getBackButton}
            mobileItems={mobileItems}
            utilityLinks={utilityLinks}
          />
        )}
        {isDesktopScreen && (
          <DesktopTopContainer
            placeholderText={placeholderText}
            logo={logo}
            utilityLinks={utilityLinks}
            logoText={logoText}
            desktopItems={desktopItems}
            giveMoneyButton={giveMoneyButton}
            joinRegistryButton={joinRegistryButton}
          />
        )}
      </div>
      {isSmallMobileScreen && (
        <MobileBottomContainer
          giveMoneyButton={giveMoneyButton}
          giveButtonMobileLabel={giveButtonMobileLabel}
          joinRegistryButton={joinRegistryButton}
          joinButtonMobileLabel={joinButtonMobileLabel}
        />
      )}
    </div>
  );
};

export default NmdpSiteHeader;
